import { consumeProgrammaticSeek, intendedPosition } from "./programmaticSeek.js"
import { trackDuration } from "./trackContract.js"

/** Beats follow a grid of playback position: one every BEAT_SECONDS while playing. */
export const BEAT_SECONDS = 15

// The position stream carries no viewing state, exactly like the podcast heartbeat.
const STATELESS = new Set(["time", "beat"])

function listen(el, handlers) {
    for (const [type, handler] of Object.entries(handlers)) {
        el.addEventListener(type, handler)
    }

    return () => {
        for (const [type, handler] of Object.entries(handlers)) {
            el.removeEventListener(type, handler)
        }
    }
}

function durationOf(track, el) {
    if (el && Number.isFinite(el.duration) && el.duration > 0) {
        return el.duration
    }

    return trackDuration(track) || null
}

/**
 * PlayerShell's one normalized event stream (SC-1323 §4.2): media element events and the shell's
 * own actions become `{ type, trackId, itemId, mode, position, duration, ...fields }`, where
 * `mode` is the kind of the track the event is about. `stateFor(el)` returns the video viewing
 * state `{ fullscreen, qualityHeight?, viewport? }`, added to video-track events.
 *
 * The element rules are the podcast beacons' (formerly VideoPlayer/beacons.js), unchanged: video
 * events fire at once; an audio `play` waits for a known duration and reports the intended
 * position; a programmatic seek is never a `seek`; the pause that comes with the end is not
 * sent; repeated `waiting` during one stall is one interval. A pause the switcher causes is
 * flagged `switching` (markSwitchPause) so the portal can leave it out.
 */
export function createPlayerEvents({ emit, stateFor = () => ({}) }) {
    const trackState = new Map()
    const switchPauses = new WeakSet()

    function publish(track, el, raw) {
        const state = trackState.get(track.id) ?? { started: false, paused: false }
        let type = raw.type

        if (type === "play") {
            type = state.started && state.paused ? "resume" : "play"
            state.started = true
            state.paused = false
        } else if (type === "pause") {
            state.paused = true
        } else if (type === "ended") {
            state.started = false
            state.paused = false
        }

        trackState.set(track.id, state)

        const viewing = track.kind === "video" && !STATELESS.has(raw.type) ? stateFor(el) : {}

        emit({ trackId: track.id, itemId: track.itemId, mode: track.kind, duration: durationOf(track, el), ...viewing, ...raw, type })
    }

    function bind(el, track) {
        const video = track.kind === "video"
        const send = (raw) => publish(track, el, raw)
        const position = () => (video ? el.currentTime || 0 : el.currentTime)
        const hasDuration = () => Number.isFinite(el.duration) && el.duration > 0
        let muted = el.muted
        let bufferingStartedAt = null
        let pendingPlay = false
        let nextBeatAt = 0
        let lastPosition = el.currentTime || 0
        let seekFrom = null

        function sendPlay(at) {
            send(video ? { type: "play", position: at } : { type: "play", position: at, duration: el.duration })
            nextBeatAt = at + BEAT_SECONDS
        }

        return listen(el, {
            play: () => {
                if (video) {
                    sendPlay(el.currentTime || 0)
                } else if (hasDuration()) {
                    // intendedPosition: a switch seek that lands on this same loadedmetadata
                    // may not have been re-applied yet when this listener runs.
                    sendPlay(intendedPosition(el))
                } else {
                    pendingPlay = true
                }
            },
            loadedmetadata: () => {
                if (!video && pendingPlay && hasDuration()) {
                    pendingPlay = false
                    sendPlay(intendedPosition(el))
                }
            },
            pause: () => {
                // A stall cut short by a pause (the switch) is not buffering: the next
                // `playing` may come minutes later.
                bufferingStartedAt = null
                pendingPlay = false

                const switching = switchPauses.has(el)
                switchPauses.delete(el)

                if (!el.ended) {
                    send({ type: "pause", position: position(), switching })
                }
            },
            ended: () => send({ type: "ended", position: position() }),
            seeking: () => {
                if (seekFrom === null) {
                    seekFrom = lastPosition
                }
            },
            seeked: () => {
                const to = position()

                if (!consumeProgrammaticSeek(el)) {
                    send({ type: "seek", position: to, from: seekFrom ?? to, to })
                }

                seekFrom = null
                lastPosition = el.currentTime || 0
                nextBeatAt = (el.currentTime || 0) + BEAT_SECONDS
            },
            volumechange: () => {
                if (el.muted === muted) {
                    return
                }

                muted = el.muted
                send({ type: muted ? "mute" : "unmute", position: position() })
            },
            timeupdate: () => {
                if (seekFrom === null) {
                    lastPosition = el.currentTime || 0
                }

                send({ type: "time", position: el.currentTime || 0, playing: !el.paused })

                if (el.paused || el.currentTime < nextBeatAt) {
                    return
                }

                send({ type: "beat", position: nextBeatAt, currentTime: el.currentTime })
                nextBeatAt = el.currentTime + BEAT_SECONDS
            },
            waiting: () => {
                // Repeated `waiting` during one stall is one interval, measured from the first.
                if (!video || bufferingStartedAt !== null) {
                    return
                }

                bufferingStartedAt = Date.now()
                send({ type: "buffering_start", position: position() })
            },
            playing: () => {
                if (!video || bufferingStartedAt === null) {
                    return
                }

                const bufferingMs = Date.now() - bufferingStartedAt
                bufferingStartedAt = null
                send({ type: "buffering_end", position: position(), bufferingMs })
            },
        })
    }

    return {
        bind,
        markSwitchPause(el) {
            switchPauses.add(el)
        },
        ctaClick(track, el, cta) {
            publish(track, el, { type: "cta_click", position: el?.currentTime ?? 0, ctaId: cta?.id ?? null })
        },
        qualitySwitch(track, el) {
            publish(track, el, { type: "quality_switch", position: el?.currentTime ?? 0 })
        },
        fullscreen(track, el, isFullscreen) {
            publish(track, el, { type: isFullscreen ? "fullscreen_enter" : "fullscreen_exit", position: el?.currentTime ?? 0 })
        },
        modeSwitch({ fromTrack, toTrack, fromPosition, fromDuration, fromEl = null }) {
            // Mixed on purpose: the top-level trackId/itemId/mode name the TARGET (the track now
            // active), while position, duration and the viewing fields (fullscreen, qualityHeight,
            // viewport) describe the track LEFT. Consumers read from*/to* for the explicit sides.
            // In a browser this event is emitted synchronously inside the click, before the queued
            // pause of the side left and play of the side started; FakeMedia fires those first.
            const viewing = fromTrack.kind === "video" ? stateFor(fromEl) : {}

            emit({
                type: "mode_switch",
                trackId: toTrack.id,
                itemId: toTrack.itemId,
                mode: toTrack.kind,
                position: fromPosition,
                duration: fromDuration,
                ...viewing,
                fromTrackId: fromTrack.id,
                toTrackId: toTrack.id,
                fromItemId: fromTrack.itemId,
                toItemId: toTrack.itemId,
                fromMode: fromTrack.kind,
                toMode: toTrack.kind,
            })
        },
    }
}
